/*
 * BreadcrumbsDropdown  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useState, MouseEvent } from "react";
import { Icon, ICON_SIZE } from "../Icon";
import { BEM } from "@adapptio/lib-components";
import { DROPDOWN_STYLE, DropdownMenu, IDropdownMenuItem } from "../DropdownMenu";
import { IBreadcrumbsItem } from "./BreadcrumbsTypes";
import { FC_ANCHOR } from "../FloatingContainer";

export interface IBreadcrumbsDropdownProps {
	items: IBreadcrumbsItem[];
}

export const BreadcrumbsCollapsed: React.FC<IBreadcrumbsDropdownProps> = ({ items }) => {
	const [ isMenuOpened, setIsMenuOpened ] = useState(false);
	const buttonRef = React.useRef();

	const dropdownItems = React.useMemo<IDropdownMenuItem[]>(
		() =>
			items.map((item, index) => ({
				id: `breadcrumb-${index}`,
				label: item.label,
				onClick: item.onClick
			})),
		[ items ]
	);

	const handleToggleClick = useCallback((ev: MouseEvent) => {
		ev.stopPropagation();
		setIsMenuOpened((s) => !s);
	}, []);

	const handleMenuClose = useCallback(() => {
		setIsMenuOpened(false);
	}, []);

	const handleMenuItemClick = useCallback(() => {
		// @todo Do something with item
		setIsMenuOpened(false);
	}, []);

	const baseClass = BEM("edt-breadcrumbs-dropdown");

	return (
		<button onClick={handleToggleClick} ref={buttonRef} className={baseClass.toClassName()}>
			<Icon
				name="mdi/dots-horizontal"
				size={ICON_SIZE.SMALL}
				className={baseClass.element("icon").toClassName()}
			/>
			<DropdownMenu
				parent={buttonRef}
				items={dropdownItems}
				isOpened={isMenuOpened}
				anchor={FC_ANCHOR.LEFT_BOTTOM}
				style={DROPDOWN_STYLE.COMPACT}
				onClose={handleMenuClose}
				onItemClick={handleMenuItemClick}
			/>
		</button>
	);
};
